import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface MessageButton {
  label: string;
  to: string;
  icon?: string;
}

interface Message {
  id: number;
  type: 'bot' | 'user';
  text: string;
  time: string;
  buttons?: MessageButton[];
}

type ConversationStage =
  | 'greeting'
  | 'asked_clients'
  | 'asked_frequency'
  | 'asked_ai_knowledge'
  | 'recommendation'
  | 'free_chat';

// Progress config for qualifying stages
const STAGE_PROGRESS: Partial<Record<ConversationStage, { step: number; label: string }>> = {
  asked_clients: { step: 1, label: 'Client volume' },
  asked_frequency: { step: 2, label: 'Enquiry frequency' },
  asked_ai_knowledge: { step: 3, label: 'AI familiarity' },
  recommendation: { step: 4, label: 'Your recommendation' },
};
const TOTAL_STEPS = 4;

const getTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const QUICK_REPLIES: Record<ConversationStage, string[]> = {
  greeting: ['Tell Me About Automation', 'Pricing Info', 'Setup Timeline', 'Book a Demo'],
  asked_clients: ['Under 10 per week', '10 to 30 per week', '30 to 60 per week', '60+ per week'],
  asked_frequency: ['Multiple times daily', 'Once or twice a day', 'A few times a week', 'A few per month'],
  asked_ai_knowledge: ['Brand new to it', 'Heard about it', 'Know a little', 'Used AI before'],
  recommendation: ['Book a Free Demo', 'View the Pro Plan', 'See All Plans', 'How does setup work?'],
  free_chat: ['Book a Free Demo', 'View the Pro Plan', 'Setup Timeline', 'How does it work?'],
};

const renderBotText = (text: string) => {
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    if (line.startsWith('## ')) {
      return (
        <p key={idx} className="text-ea-blue font-bold text-sm mt-3 first:mt-0 leading-snug">
          {line.replace('## ', '')}
        </p>
      );
    }
    if (line.startsWith('- ')) {
      return (
        <div key={idx} className="flex items-start gap-2 mt-1">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-check-line text-ea-blue text-xs"></i>
          </div>
          <p className="text-sm leading-relaxed">{line.replace('- ', '')}</p>
        </div>
      );
    }
    if (line.trim() === '') {
      return <div key={idx} className="h-1.5" />;
    }
    return (
      <p key={idx} className="text-sm leading-relaxed">
        {line}
      </p>
    );
  });
};

const isDirectQuestion = (text: string): boolean => {
  const lower = text.toLowerCase();
  return (
    lower.includes('demo') ||
    lower.includes('book') ||
    lower.includes('pric') ||
    lower.includes('cost') ||
    lower.includes('plan') ||
    lower.includes('setup') ||
    lower.includes('timeline') ||
    lower.includes('how long') ||
    lower.includes('how does') ||
    lower.includes('how it') ||
    lower.includes('what is') ||
    lower.includes('tell me about') ||
    lower.includes('automation') ||
    lower.includes('booking system')
  );
};

const getFreeReply = (text: string): { reply: string; buttons?: MessageButton[] } => {
  const lower = text.toLowerCase();

  if (lower.includes('demo') || lower.includes('book a demo')) {
    return {
      reply: `## Book a Free Demo\n\nYou can book your free 30-minute demo directly on our contact page.\n\nWe will walk you through exactly what automation looks like for your specific business. No commitment and no jargon.`,
      buttons: [{ label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' }],
    };
  }

  if (lower.includes('pric') || lower.includes('cost') || lower.includes('plan')) {
    return {
      reply: `## Our Plans\n\n## Pro (Most Popular, Most Recommended)\n£299/mo. One-time setup: £399 to £699\nA fully automated sales and booking system working across all channels. The right choice for most businesses.\n\n## Scale\n£599/mo. One-time setup: £799 to £1,499\nA complete AI-powered system for high-volume businesses needing custom workflows and deeper integrations.\n\n## Custom Pricing\nOne-time setup from £1,000+. Fully tailored automation built around your exact business requirements.\n\nEvery plan includes ongoing monthly support and optimisation. We build and manage everything for you.`,
      buttons: [
        { label: 'View All Plans', to: '/pricing', icon: 'ri-price-tag-3-line' },
        { label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' },
      ],
    };
  }

  if (lower.includes('setup') || lower.includes('timeline') || lower.includes('how long') || lower.includes('days')) {
    return {
      reply: `## Setup Timeline\n\nPro builds typically take 30 to 45 days.\n\nScale and Custom systems can take up to 60 days depending on the depth of configuration and integrations involved.\n\nWe handle everything from start to finish. No technical knowledge required from you at any point.`,
      buttons: [{ label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' }],
    };
  }

  if (lower.includes('how') || lower.includes('automation') || lower.includes('tell me') || lower.includes('what')) {
    return {
      reply: `## What Is AI Automation?\n\nAI automation means your business captures leads, responds to enquiries, books appointments, and follows up with customers automatically.\n\nYou do not need to be available around the clock. Your system is.\n\n## What It Actually Does\n\n- Responds to every enquiry instantly, even late at night\n- Books appointments directly into your calendar\n- Sends reminders to cut down on no-shows\n- Follows up with leads that have gone quiet\n- Collects reviews automatically after every job\n\nWe build and manage the entire system for you. No technical skills needed.\n\nWant to see how it would work specifically for your business? Tell me a bit about what you do.`,
    };
  }

  if (lower.includes('booking system') || lower.includes('appointment') || lower.includes('calendar')) {
    return {
      reply: `## Booking Automation\n\nOur booking system connects directly with your calendar.\n\nEvery new booking gets a confirmation, a reminder 48 hours before, and another 2 hours before. Clients can confirm or reschedule with one tap.\n\nThis alone typically cuts no-shows by 35 to 40%.`,
      buttons: [{ label: 'See How It Works', to: '/industries', icon: 'ri-arrow-right-line' }],
    };
  }

  return {
    reply: `I am here to help you work out which automation setup is right for your business.\n\nTell me a bit about what you do and I will ask a few quick questions to give you something genuinely useful.`,
  };
};

const buildRecommendation = (
  clientVolume: string,
  aiKnowledge: string
): { text: string; buttons: MessageButton[] } => {
  const isHighVolume =
    clientVolume.toLowerCase().includes('60') ||
    clientVolume.toLowerCase().includes('many') ||
    clientVolume.toLowerCase().includes('lots');

  const isNewToAI =
    aiKnowledge.toLowerCase().includes('new') ||
    aiKnowledge.toLowerCase().includes('brand') ||
    aiKnowledge.toLowerCase().includes('heard') ||
    aiKnowledge.toLowerCase().includes('little');

  const aiIntro = isNewToAI
    ? `AI automation means your business handles enquiries, bookings, and follow-ups automatically around the clock without you needing to be involved. We build and manage the entire system for you. There is nothing technical you need to learn or do.`
    : `Based on what you have shared, here is exactly how automation would fit into your operation and where the biggest gains would come from.`;

  const planSection = isHighVolume
    ? `## Our Recommendation for You\n\nGiven the volume you are handling, the Scale Plan is the right fit.\n\nScale is £599/mo with a one-time setup from £799 to £1,499. It gives you fully custom workflows built around how your business actually runs, unlimited integrations, a dedicated account manager, priority support, and monthly optimisation so the system keeps improving over time.\n\nThis is not software. We build it, we run it, and we keep it performing.`
    : `## Our Recommendation for You\n\nThe Pro Plan is the right fit for a business like yours.\n\nPro is £299/mo with a one-time setup from £399 to £699.\n\n- Multi-channel automation across WhatsApp, Instagram, Facebook, SMS, and Email\n- Full booking system with confirmations and reminders built in\n- Automated follow-up sequences that turn cold enquiries into paying clients\n- CRM pipeline so no lead ever falls through the cracks\n- Monthly optimisation included every single month\n\nThis is a done-for-you system. We build it, we maintain it, and we keep it improving. You just run your business.`;

  const text = `${aiIntro}

## How It Benefits a Business Like Yours

- Every enquiry gets an instant response, even at midnight on a Sunday
- You never lose a lead to a slow reply again
- Booking reminders cut no-shows by up to 40%
- Follow-up sequences automatically recover cold enquiries
- Hours of weekly admin are handled without you touching anything

${planSection}

Ready to see exactly what this looks like for your business?`;

  const buttons: MessageButton[] = [
    { label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' },
    {
      label: isHighVolume ? 'View Scale Plan' : 'View Pro Plan',
      to: '/pricing',
      icon: 'ri-price-tag-3-line',
    },
    { label: 'See All Plans', to: '/pricing', icon: 'ri-layout-grid-line' },
  ];

  return { text, buttons };
};

const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<ConversationStage>('greeting');
  const [clientVolume, setClientVolume] = useState('');
  const [businessDesc, setBusinessDesc] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      type: 'bot',
      text: `## Welcome to EA Assistant\n\nI am here to help you figure out whether AI automation is right for your business and exactly which setup would suit you best.\n\nTell me a bit about what you do and I will take it from there.`,
      time: getTime(),
    },
  ]);
  const [input, setInput] = useState('');
  const [hasNotification, setHasNotification] = useState(true);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const resetConversation = () => {
    setStage('greeting');
    setClientVolume('');
    setBusinessDesc('');
    setMessages([
      {
        id: Date.now(),
        type: 'bot',
        text: `## Welcome to EA Assistant\n\nI am here to help you figure out whether AI automation is right for your business and exactly which setup would suit you best.\n\nTell me a bit about what you do and I will take it from there.`,
        time: getTime(),
      },
    ]);
    setInput('');
    setTyping(false);
  };

  useEffect(() => {
    if (open) setHasNotification(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, messages]);

  const pushBotMessage = (text: string, buttons?: MessageButton[]) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now() + 1, type: 'bot', text, time: getTime(), buttons },
    ]);
    setTyping(false);
  };

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), type: 'user', text, time: getTime() },
    ]);
    setInput('');
    setTyping(true);

    setTimeout(() => {
      if (stage === 'greeting') {
        if (isDirectQuestion(text)) {
          const { reply, buttons } = getFreeReply(text);
          pushBotMessage(reply, buttons);
        } else if (text.trim().length > 6) {
          setBusinessDesc(text);
          setStage('asked_clients');
          pushBotMessage(
            `Great, thanks for sharing that!\n\nBefore I point you in the right direction, I have a couple of quick questions so I can give you something genuinely useful rather than a generic answer.\n\nRoughly how many clients, customers, or jobs do you handle in a typical week?`
          );
        } else {
          const { reply, buttons } = getFreeReply(text);
          pushBotMessage(reply, buttons);
        }
      } else if (stage === 'asked_clients') {
        setClientVolume(text);
        setStage('asked_frequency');
        pushBotMessage(
          `Got it, that is helpful to know.\n\nAnd how often do you typically receive new enquiries or booking requests? Is it something you are dealing with every single day, or more spread out across the week?`
        );
      } else if (stage === 'asked_frequency') {
        setStage('asked_ai_knowledge');
        pushBotMessage(
          `Almost there, just one more.\n\nHow familiar are you with AI automation? Have you set something like this up before, or is this something you are exploring for the first time?`
        );
      } else if (stage === 'asked_ai_knowledge') {
        setStage('recommendation');
        const { text: recText, buttons } = buildRecommendation(clientVolume, text);
        pushBotMessage(recText, buttons);
      } else {
        const lower = text.toLowerCase();
        if ((lower.includes('view') || lower.includes('see')) && lower.includes('pro')) {
          pushBotMessage(
            `## Pro Plan Summary\n\nPro is our most popular plan and the right fit for most businesses.\n\n- £299/mo\n- One-time setup: £399 to £699\n- Multi-channel automation (WhatsApp, Instagram, Facebook, SMS, Email)\n- Full booking system with confirmations and reminders\n- Automated follow-up sequences\n- CRM pipeline and lead tracking\n- Monthly optimisation included`,
            [
              { label: 'View Full Pro Plan', to: '/pricing', icon: 'ri-price-tag-3-line' },
              { label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' },
            ]
          );
        } else if (lower.includes('scale')) {
          pushBotMessage(
            `## Scale Plan Summary\n\nScale is built for businesses handling higher volumes or needing deeper custom automation.\n\n- £599/mo\n- One-time setup: £799 to £1,499\n- Fully custom automation workflows\n- Unlimited integrations\n- Dedicated account manager\n- Staff training and onboarding\n- Priority support and monthly optimisation`,
            [
              { label: 'View Full Scale Plan', to: '/pricing', icon: 'ri-price-tag-3-line' },
              { label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' },
            ]
          );
        } else if (lower.includes('all plans') || lower.includes('see plans')) {
          pushBotMessage(
            `## All Plans\n\n## Pro (Most Recommended)\n£299/mo. Setup: £399 to £699. Best for most businesses.\n\n## Scale\n£599/mo. Setup: £799 to £1,499. Best for higher-volume operations.\n\n## Custom Pricing\nSetup from £1,000+. Built entirely around your business.`,
            [
              { label: 'View Pricing Page', to: '/pricing', icon: 'ri-price-tag-3-line' },
              { label: 'Book a Free Demo', to: '/contact', icon: 'ri-calendar-line' },
            ]
          );
        } else {
          setStage('free_chat');
          const { reply, buttons } = getFreeReply(text);
          pushBotMessage(reply, buttons);
        }
      }
    }, 1100);
  };

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col items-start gap-3">
      {open && (
        <div
          className="w-80 sm:w-96 h-[580px] bg-ea-dark rounded-2xl border border-ea-dark-border flex flex-col overflow-hidden"
          style={{ boxShadow: '0 0 40px rgba(14, 165, 233, 0.15)' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-ea-blue-dark to-ea-blue px-4 py-3.5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-robot-2-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">EA Assistant</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                  <p className="text-white/80 text-xs">Typically replies instantly</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={resetConversation}
                title="Restart conversation"
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 text-white/70 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-restart-line text-base"></i>
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Progress Bar — shown only during qualifying stages */}
          {STAGE_PROGRESS[stage] && (
            <div className="px-4 py-2.5 bg-ea-dark border-b border-ea-dark-border flex-shrink-0">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-ea-blue text-xs font-semibold">
                  Step {STAGE_PROGRESS[stage]!.step} of {TOTAL_STEPS}
                  <span className="text-ea-gray font-normal ml-1.5">&middot; {STAGE_PROGRESS[stage]!.label}</span>
                </span>
                {stage !== 'recommendation' && (
                  <span className="text-ea-gray/50 text-xs">
                    {TOTAL_STEPS - STAGE_PROGRESS[stage]!.step} more to go
                  </span>
                )}
                {stage === 'recommendation' && (
                  <span className="text-green-400 text-xs font-medium flex items-center gap-1">
                    <i className="ri-check-line text-xs"></i> Done
                  </span>
                )}
              </div>
              <div className="w-full bg-ea-dark-card rounded-full h-1.5">
                <div
                  className="bg-ea-blue h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${(STAGE_PROGRESS[stage]!.step / TOTAL_STEPS) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[86%] rounded-2xl px-4 py-3 ${
                    msg.type === 'user'
                      ? 'bg-ea-blue text-white rounded-tr-sm'
                      : 'bg-ea-dark-card border border-ea-dark-border text-white rounded-tl-sm'
                  }`}
                >
                  {msg.type === 'bot' ? (
                    <>
                      <div className="space-y-0.5">{renderBotText(msg.text)}</div>
                      {msg.buttons && msg.buttons.length > 0 && (
                        <div className="mt-3 flex flex-col gap-2">
                          {msg.buttons.map((btn) => (
                            <Link
                              key={btn.label}
                              to={btn.to}
                              onClick={() => setOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 bg-ea-blue hover:bg-ea-blue-light text-white text-xs font-semibold rounded-lg transition-all duration-200 cursor-pointer"
                            >
                              {btn.icon && (
                                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                  <i className={`${btn.icon} text-sm`}></i>
                                </div>
                              )}
                              <span className="flex-1">{btn.label}</span>
                              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                <i className="ri-arrow-right-line text-xs"></i>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  )}
                  <p className={`text-xs mt-2 ${msg.type === 'user' ? 'text-white/60' : 'text-ea-gray'}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-ea-dark-card border border-ea-dark-border rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-ea-blue animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-ea-blue animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-ea-blue animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {QUICK_REPLIES[stage].map((qr) => (
              <button
                key={qr}
                onClick={() => sendMessage(qr)}
                className="px-3 py-1 text-xs rounded-full border border-ea-blue/40 text-ea-blue hover:bg-ea-blue hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {qr}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-ea-dark-border flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
              placeholder={
                stage === 'greeting'
                  ? 'Tell me about your business...'
                  : stage === 'asked_clients'
                  ? 'How many clients per week?'
                  : stage === 'asked_frequency'
                  ? 'How often do you get enquiries?'
                  : stage === 'asked_ai_knowledge'
                  ? 'Your experience with AI...'
                  : 'Type a message...'
              }
              className="flex-1 bg-ea-dark-card border border-ea-dark-border rounded-lg px-3 py-2 text-white text-sm placeholder-ea-gray/60 focus:outline-none focus:border-ea-blue transition-colors"
            />
            <button
              onClick={() => sendMessage(input)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-ea-blue hover:bg-ea-blue-light text-white transition-all duration-200 cursor-pointer flex-shrink-0"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full bg-ea-blue hover:bg-ea-blue-light text-white flex items-center justify-center transition-all duration-300 cursor-pointer relative"
        style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.4)' }}
        aria-label="Open chat assistant"
      >
        {hasNotification && !open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-ea-black flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">1</span>
          </span>
        )}
        <i className={`text-2xl transition-all duration-300 ${open ? 'ri-close-line' : 'ri-robot-2-line'}`}></i>
      </button>
    </div>
  );
};

export default ChatbotWidget;
